// Accounting / PowerOffice / reconciliation types (split from storage-types.ts).

import type { AuditMetadata } from './core-types';

// Bank transaction from CSV import
export interface BankTransaction {
  id: string; // Stable ID from Numref+Arkivref or hash
  accountId: string; // Bank account number
  bookingDate: string; // ISO date
  valueDate?: string; // ISO date
  paymentTimestamp?: string; // ISO datetime
  amount: number; // Negative = money out, positive = money in
  currency: string;
  description: string;
  type?: string; // Transaction type (Varekjøp, Betaling innland, etc.)
  subtype?: string;
  counterpartyName?: string;
  referenceText?: string; // Melding/KID/Fakt.nr
  kid?: string;
  invoiceNo?: string;
  fromAccount?: string;
  toAccount?: string;
  numRef?: string;
  archiveRef?: string;
  status?: string;
  raw?: Record<string, string>; // Full parsed row for debugging
}

// PowerOffice voucher (fetched from API)
export interface PowerOfficeVoucher {
  id: string;
  voucherId: string;
  voucherNumber?: number;
  voucherDate: string; // ISO date
  totalAmount?: number;
  text?: string;
  reference?: string;
  supplierName?: string;
  lastChangedDateTimeOffset?: string;
  hasAttachments?: boolean;
  attachmentCheckedAt?: string;
  raw?: Record<string, unknown>;
}

// Match between a bank transaction and a voucher
export type ReconcileStatus = 'NO_VOUCHER' | 'MISSING_BILAG' | 'OK' | 'EXEMPT' | 'UNSURE';

export interface ReconcileMatch {
  id: string; // Same as bankTxId
  bankTxId: string;
  voucherId?: string;
  confidence: number; // 0-100
  reason: string;
  status: ReconcileStatus;
  userOverridden?: boolean;
}

// Exemption rule - transactions matching these are auto-marked as EXEMPT
export interface ExemptionRule {
  id: string;
  enabled: boolean;
  field: 'description' | 'counterpartyName' | 'referenceText';
  operator: 'contains' | 'startsWith' | 'regex';
  value: string;
  appliesTo: 'outgoing' | 'incoming' | 'both';
}

// Upload destination configuration - where each report type gets directed
export type ReportType = 'shifts' | 'terminal' | 'travel' | 'lonn';

export interface UploadDestination {
  reportType: ReportType;
  target: 'bilagsforing' | 'shared_documents' | 'custom';
  customPath?: string; // For custom target
  enabled: boolean;
}

export interface AccountingConfig extends AuditMetadata {
  // PowerOffice connection (stored server-side, this is the client-visible subset)
  isConnected: boolean;
  environment: 'demo' | 'prod';
  lastSyncAt?: string;
  // Upload destinations per report type
  uploadDestinations: UploadDestination[];
  // Exemption rules for bank reconciliation
  exemptionRules: ExemptionRule[];
}

// ============================================================================
// Vehicle Intelligence Types
// ============================================================================

// Norwegian tax and cost constants for taxi industry
export var NORWEGIAN_TAX_CONSTANTS = {
  SALDO_GROUP_C_RATE: 0.24,            // 24% annual declining balance (saldogruppe C)
  TAXI_MVA_RATE: 0.12,                 // 12% MVA for passenger transport
  STANDARD_MVA_RATE: 0.25,             // 25% standard MVA rate
  EMPLOYER_TAX_ZONE1: 0.141,           // 14.1% arbeidsgiveravgift (sone 1)
  HOLIDAY_PAY_RATE: 0.102,             // 10.2% feriepenger (minimum)
  OTP_MINIMUM_RATE: 0.02,             // 2% OTP minimum
} as const;
